import { formatCourseMoneyEnIn, type PricingPlan } from "../api";
import type { LibraryCourse } from "@/features/dashboard/types";
import {
  getEffectiveOwnedPlan,
  pickLegacyEquivalentPlan,
  type OwnedState,
} from "./resolve-course-plan-owned-state";

/** Compare Mongo/ObjectId strings safely for plan graph edges. */
export function normalizePricingPlanId(id: string | null | undefined): string | null {
  if (id == null || typeof id !== "string") return null;
  const t = id.trim();
  return t.length > 0 ? t : null;
}

/**
 * Effective tier price for upgrade math — mirrors `UpgradeClient` `priceOf`
 * (sale when valid & below list; free → 0).
 */
export function effectivePlanPrice(plan: PricingPlan): number {
  const { price } = plan;
  if (price.isFree) return 0;
  if (
    typeof price.sale === "number" &&
    Number.isFinite(price.sale) &&
    price.sale >= 0 &&
    price.sale < price.list
  ) {
    return price.sale;
  }
  return price.list;
}

export type PlanPricingUiKind =
  | "UNOWNED"
  | "OWNED"
  | "UPGRADE_TARGET"
  | "LOWER_OR_SIDEWAYS";

export type CoursePlanPricingUiModel = {
  /** Library `pricingPlanId` resolved against current `plans[]`. */
  ownedPlanInCatalog: PricingPlan | null;
  /** True when the library row has no `pricingPlanId` (pre-plan enrollment). */
  isLegacyEnrollmentWithoutPlanId: boolean;
  /** Nominal tier for legacy enrollment (`pickLegacyEquivalentPlan`). */
  nominalLegacyPlan: PricingPlan | null;
  /**
   * Plan row whose `upgradeableTo[]` defines the upgrade graph (owned catalog
   * tier, or nominal legacy tier). Null when only library `upgradeTargets`
   * fallback applies (orphan `pricingPlanId`).
   */
  upgradeGraphSourcePlan: PricingPlan | null;
  /** Candidate `_id`s that are active upgrade targets for this owner. */
  activeUpgradeTargetIds: ReadonlySet<string>;
  hasActiveUpgradeTarget: boolean;
  /** Display name resolution for “your tier” copy (never uses accessScope). */
  effectiveOwnedPlanForCopy: PricingPlan | null;
};

function collectActiveUpgradeTargets(
  plans: PricingPlan[],
  upgradeGraphSourcePlan: PricingPlan | null,
  ownedCourse: Pick<LibraryCourse, "pricingPlanId" | "upgradeTargets"> | null | undefined,
): Set<string> {
  const ids = new Set<string>();
  const planIds = new Set(plans.map((p) => normalizePricingPlanId(p._id)).filter(Boolean) as string[]);

  const addIfListed = (raw: string | undefined | null) => {
    const n = normalizePricingPlanId(raw);
    if (n && planIds.has(n)) ids.add(n);
  };

  for (const raw of upgradeGraphSourcePlan?.upgradeableTo ?? []) {
    addIfListed(raw);
  }

  const orphan =
    !!normalizePricingPlanId(ownedCourse?.pricingPlanId) &&
    !plans.some(
      (p) =>
        normalizePricingPlanId(p._id) === normalizePricingPlanId(ownedCourse?.pricingPlanId),
    );
  if (orphan && ownedCourse?.upgradeTargets?.length) {
    for (const t of ownedCourse.upgradeTargets) {
      addIfListed(t.planId);
    }
  }

  return ids;
}

/**
 * Single source of truth for course-detail pricing strip ownership / upgrades.
 * Driven by `pricingPlanId` + active `plans[]` + `upgradeableTo[]` (+ library
 * `upgradeTargets` only as orphan fallback). Does **not** use `accessScope`.
 */
export function buildCoursePlanPricingUiModel(input: {
  plans: PricingPlan[];
  ownedCourse: Pick<LibraryCourse, "pricingPlanId" | "upgradeTargets"> | null | undefined;
  isAuthenticated: boolean;
  isOwned: boolean;
}): CoursePlanPricingUiModel {
  const { plans, ownedCourse, isAuthenticated, isOwned } = input;
  const nominalLegacyPlan = plans.length > 0 ? pickLegacyEquivalentPlan(plans) : null;

  const empty: CoursePlanPricingUiModel = {
    ownedPlanInCatalog: null,
    isLegacyEnrollmentWithoutPlanId: false,
    nominalLegacyPlan,
    upgradeGraphSourcePlan: null,
    activeUpgradeTargetIds: new Set(),
    hasActiveUpgradeTarget: false,
    effectiveOwnedPlanForCopy: null,
  };

  if (!isAuthenticated || !isOwned || plans.length === 0) {
    return empty;
  }

  const libPlanId = normalizePricingPlanId(ownedCourse?.pricingPlanId);
  const ownedPlanInCatalog = libPlanId
    ? plans.find((p) => normalizePricingPlanId(p._id) === libPlanId) ?? null
    : null;

  const isLegacyEnrollmentWithoutPlanId = !libPlanId;

  const upgradeGraphSourcePlan: PricingPlan | null = ownedPlanInCatalog
    ? ownedPlanInCatalog
    : isLegacyEnrollmentWithoutPlanId
      ? nominalLegacyPlan
      : null;

  const activeUpgradeTargetIds = collectActiveUpgradeTargets(
    plans,
    upgradeGraphSourcePlan,
    ownedCourse,
  );

  const effectiveOwnedPlanForCopy: PricingPlan | null =
    ownedPlanInCatalog ??
    (isLegacyEnrollmentWithoutPlanId ? nominalLegacyPlan : null) ??
    (libPlanId ? getEffectiveOwnedPlan(plans, libPlanId) : null);

  return {
    ownedPlanInCatalog,
    isLegacyEnrollmentWithoutPlanId,
    nominalLegacyPlan,
    upgradeGraphSourcePlan,
    activeUpgradeTargetIds,
    hasActiveUpgradeTarget: activeUpgradeTargetIds.size > 0,
    effectiveOwnedPlanForCopy,
  };
}

export function resolvePlanPricingUiKind(
  candidate: PricingPlan,
  model: CoursePlanPricingUiModel,
  ctx: { isAuthenticated: boolean; isOwned: boolean },
): PlanPricingUiKind {
  if (!ctx.isAuthenticated || !ctx.isOwned) return "UNOWNED";

  const cid = normalizePricingPlanId(candidate._id);
  if (!cid) return "LOWER_OR_SIDEWAYS";

  if (model.isLegacyEnrollmentWithoutPlanId) {
    const nominalId = normalizePricingPlanId(model.nominalLegacyPlan?._id);
    if (nominalId && cid === nominalId) return "OWNED";
    if (model.activeUpgradeTargetIds.has(cid)) return "UPGRADE_TARGET";
    return "LOWER_OR_SIDEWAYS";
  }

  const ownedId = normalizePricingPlanId(model.ownedPlanInCatalog?._id);
  if (ownedId && cid === ownedId) return "OWNED";
  if (model.activeUpgradeTargetIds.has(cid)) return "UPGRADE_TARGET";
  return "LOWER_OR_SIDEWAYS";
}

/**
 * Maps pricing UI graph to existing `OwnedState` so tier cards / badges stay stable.
 */
export function resolvePricingUiOwnedState(
  plan: PricingPlan,
  model: CoursePlanPricingUiModel,
  ctx: { isAuthenticated: boolean; isOwned: boolean },
): OwnedState {
  const kind = resolvePlanPricingUiKind(plan, model, ctx);
  switch (kind) {
    case "UNOWNED":
      return { kind: "not-owned" };
    case "OWNED":
      return model.isLegacyEnrollmentWithoutPlanId
        ? { kind: "owned-legacy-full" }
        : { kind: "owned-this" };
    case "UPGRADE_TARGET":
      return { kind: "owned-other", upgradeable: true };
    case "LOWER_OR_SIDEWAYS":
      return model.isLegacyEnrollmentWithoutPlanId
        ? { kind: "owned-legacy-peer" }
        : { kind: "owned-other", upgradeable: false };
  }
}

export function isPlanIdActiveUpgradeTarget(
  planId: string | undefined | null,
  model: CoursePlanPricingUiModel,
): boolean {
  const n = normalizePricingPlanId(planId);
  return !!n && model.activeUpgradeTargetIds.has(n);
}

/** Localized upgrade CTA label with pay differential (UpgradeClient-style math). */
export function formatTierUpgradeCtaLabel(
  candidatePlan: PricingPlan,
  model: CoursePlanPricingUiModel,
  labels: {
    upgradePay: (amount: string) => string;
    upgradeFree: () => string;
  },
): string {
  const src = model.upgradeGraphSourcePlan;
  const base = src ? effectivePlanPrice(src) : 0;
  const diff = Math.max(0, effectivePlanPrice(candidatePlan) - base);
  if (diff === 0) return labels.upgradeFree();
  const sym = candidatePlan.price.currency === "BDT" ? "৳" : "$";
  const amount = `${sym}${formatCourseMoneyEnIn(diff)}`;
  return labels.upgradePay(amount);
}

export function isSidebarPlanRowOwnedTier(
  plan: PricingPlan,
  model: CoursePlanPricingUiModel,
): boolean {
  const pid = normalizePricingPlanId(plan._id);
  if (!pid) return false;
  if (
    model.ownedPlanInCatalog &&
    normalizePricingPlanId(model.ownedPlanInCatalog._id) === pid
  ) {
    return true;
  }
  if (
    model.isLegacyEnrollmentWithoutPlanId &&
    model.nominalLegacyPlan &&
    normalizePricingPlanId(model.nominalLegacyPlan._id) === pid
  ) {
    return true;
  }
  return false;
}
